import { z } from 'zod';
import { CalendarDate } from './project.js';
/**
 * Kế hoạch nháp do AI sinh — APM-602, spec mục 9.
 *
 * Nháp **không phải** task. Nó chỉ thành task thật khi người bấm "Áp dụng"
 * (ADR-005, luật #6). Trước đó nó là thứ sửa được từng dòng.
 */
/**
 * `clarifying` — APM-806: nháp **chưa có hạng mục nào**, đang chờ người dùng
 * trả lời câu hỏi của AI.
 *
 * Là một trạng thái thật trong DB chứ không phải state ở trình duyệt: người
 * dùng đóng tab giữa chừng là chuyện thường, và một wizard mất trắng thì lần
 * sau không ai dùng.
 */
export const PlanDraftStatus = z.enum(['clarifying', 'pending', 'applied', 'discarded']);
/**
 * Hình dạng model **phải** trả về.
 *
 * Phụ thuộc khai bằng **chỉ số trong mảng**, không phải id: model không biết id
 * nào có thật, và bắt nó đoán là mời nó bịa ra. Chỉ số thì nó tự đếm được, và
 * ta kiểm tra được ngay.
 */
export const GeneratedItem = z.object({
    title: z.string().min(1).max(500),
    description: z.string().max(5_000).optional(),
    /** Slug loại task. Khớp sang danh mục tổ chức lúc áp dụng, sai thì bỏ qua. */
    taskTypeSlug: z.string().max(60).optional(),
    techStackSlug: z.string().max(60).optional(),
    estimateHours: z.number().positive().max(2_000),
    /** Vì sao ước lượng như vậy — spec mục 9 đòi con số đi kèm lý do. */
    estimateRationale: z.string().min(1).max(500),
    startOffsetDays: z.number().int().min(0).max(3_650),
    durationDays: z.number().int().min(1).max(365),
    /**
     * Chỉ số các hạng mục phải xong trước, trong cùng mảng này.
     *
     * Cố ý **không** chặn chỉ số ngoài khoảng ở đây: handler bỏ riêng cạnh sai và
     * giữ phần còn lại. Chặn ở schema nghĩa là model đếm nhầm một lần thì cả kế
     * hoạch 60 hạng mục bị vứt — và sinh lại là tiền thật.
     */
    dependsOn: z.array(z.number().int()).max(10).optional(),
    /** Subtask mức giờ. Bỏ trống ở tầng 1 nếu chưa cần chia nhỏ. */
    subtasks: z
        .array(z.object({
        title: z.string().min(1).max(500),
        estimateHours: z.number().positive().max(500),
    }))
        .max(12)
        .optional(),
});
/**
 * ⚠️ Kế hoạch KHÔNG CẠNH NÀO bị từ chối — APM-1314.
 *
 * Không có phụ thuộc thì đồ thị rời rạc, và với đồ thị rời rạc thì "đường găng"
 * là **mọi** hạng mục — tức là không có đường găng. Người duyệt mất đúng thứ
 * quan trọng nhất cần biết trước khi bấm Áp dụng: chuỗi việc nào quyết định
 * ngày về đích.
 *
 * Nhưng chú ý cái **không** làm ở đây: không ép mỗi hạng mục phải khai
 * `dependsOn`. Ép thì model sẽ bịa phụ thuộc cho đủ schema, và một cạnh bịa còn
 * tệ hơn không có cạnh — nó kéo dài đường găng bằng một ràng buộc không tồn
 * tại. Việc thật sự chạy song song thì cứ để rời; chỉ cần cả kế hoạch có ít
 * nhất một quan hệ trước–sau, vì một kế hoạch 20 việc mà không việc nào phụ
 * thuộc việc nào thì gần như chắc chắn là model chưa nghĩ đến thứ tự.
 *
 * Chỉ số sai (trỏ ra ngoài mảng, trỏ vào chính nó) vẫn **không** làm hỏng cả kế
 * hoạch — chúng bị bỏ ở tầng ghi. Chỗ này chỉ đếm cạnh **có thể dùng được**,
 * đúng bằng những cạnh sẽ sống sót.
 */
export const GeneratedPlan = z
    .object({
    /** Model giải thích cách nó chia việc — hiện ở đầu màn hình review. */
    rationale: z.string().min(1).max(4_000),
    items: z.array(GeneratedItem).min(1).max(60),
})
    .superRefine((value, ctx) => {
    if (value.items.length < 2)
        return;
    const usable = value.items.some((item, index) => (item.dependsOn ?? []).some((dep) => dep !== index && dep >= 0 && dep < value.items.length));
    if (!usable) {
        ctx.addIssue({
            code: 'custom',
            path: ['items'],
            message: 'Kế hoạch phải có ít nhất một quan hệ phụ thuộc (`dependsOn`) để tính được đường găng',
        });
    }
});
// ── Đọc ra ──────────────────────────────────────────────────────────────────
export const PlanDraftItemView = z.object({
    id: z.uuid(),
    parentId: z.uuid().nullable(),
    title: z.string(),
    description: z.string().nullable(),
    taskTypeSlug: z.string().nullable(),
    techStackSlug: z.string().nullable(),
    estimateHours: z.number().nullable(),
    estimateRationale: z.string().nullable(),
    suggestedAssigneeId: z.uuid().nullable(),
    suggestedAssigneeName: z.string().nullable(),
    startOffsetDays: z.number().int().nullable(),
    durationDays: z.number().int().nullable(),
    position: z.number().int().nonnegative(),
    /** Người review bỏ hạng mục này ra khỏi lần áp dụng. */
    excluded: z.boolean(),
    dependsOn: z.array(z.uuid()),
    /**
     * Nằm trên đường găng — APM-1314.
     *
     * Tính ở **server**, bằng đúng `computeCriticalPath` mà Gantt thật dùng.
     * Đoán lại ở client là dựng bản CPM thứ hai, và hai bản lệch nhau thì bản xem
     * trước tô một chuỗi việc khác hẳn bản sau khi áp dụng — đúng lúc người dùng
     * cần chúng giống nhau để tin vào nó.
     */
    critical: z.boolean(),
    /** Trễ được bao nhiêu ngày mà không đẩy ngày kết thúc. `0` = găng. */
    totalFloat: z.number().int(),
});
/** Một lượt tinh chỉnh: người dùng nói gì, model trả lời ra sao. */
export const PlanRefinement = z.object({
    at: z.iso.datetime(),
    instruction: z.string(),
    rationale: z.string(),
});
/**
 * Một câu hỏi mà AI hỏi lại trước khi sinh kế hoạch — APM-806.
 *
 * ## Vì sao `recommended` là **bắt buộc**
 *
 * Hỏi mà không gợi ý là đẩy việc suy nghĩ ngược cho người dùng, và họ sẽ bấm
 * bừa cho xong — lúc đó câu hỏi còn tệ hơn là không hỏi. `why` phải nói **căn
 * cứ** (tech stack đã khai, template đang dùng, velocity lịch sử), cùng tinh
 * thần với `evidence` của `risk_scan`.
 */
export const ClarifyQuestion = z.object({
    /** Khoá ổn định để ghép câu trả lời. Model tự đặt, dạng `snake_case`. */
    key: z.string().min(1).max(60),
    question: z.string().min(1).max(500),
    /** Vì sao câu này quan trọng, và căn cứ nào dẫn tới khuyến nghị. */
    why: z.string().min(1).max(500),
    /** Đáp án khuyến nghị — điền sẵn vào ô trả lời. */
    recommended: z.string().min(1).max(500),
    /** Lựa chọn nhanh, nếu câu hỏi có tập đáp án rõ ràng. Luôn kèm ô tự gõ. */
    options: z.array(z.string().min(1).max(200)).max(6).default([]),
});
/** Trần số câu hỏi một lượt. Nhiều hơn thì đó là một bản khảo sát, không phải làm rõ. */
export const MAX_CLARIFY_QUESTIONS = 5;
export const PlanClarify = z.object({
    questions: z.array(ClarifyQuestion).min(1).max(MAX_CLARIFY_QUESTIONS),
    /** `null` = chưa trả lời. Khoá là `ClarifyQuestion.key`. */
    answers: z.record(z.string(), z.string()).nullable(),
    askedAt: z.iso.datetime(),
    answeredAt: z.iso.datetime().nullable(),
});
export const PlanDraftView = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    status: PlanDraftStatus,
    templateId: z.uuid().nullable(),
    prompt: z.string(),
    rationale: z.string().nullable(),
    toolsUsed: z.array(z.string()),
    costUsd: z.string(),
    /** Hội thoại tinh chỉnh, cũ trước mới sau. Rỗng khi chưa tinh chỉnh lần nào. */
    refinements: z.array(PlanRefinement),
    items: z.array(PlanDraftItemView),
    /**
     * Vòng hỏi lại — APM-806. `null` = AI không cần hỏi gì.
     *
     * Còn giá trị sau khi đã trả lời: câu hỏi và câu trả lời là **căn cứ** của
     * bản kế hoạch, và người duyệt cần đọc được chúng để biết nháp này dựa trên
     * giả định nào.
     */
    clarify: PlanClarify.nullable(),
    /**
     * Lượt chạy đã sinh ra nháp này — APM-UX-ai2. `null` với nháp cũ.
     *
     * Cột trợ lý dùng nó để hiện câu hỏi **dưới chính luồng suy nghĩ** đã dẫn tới
     * câu hỏi ấy, thay vì bày câu hỏi trần ở một danh sách riêng.
     */
    runId: z.uuid().nullable(),
    /** Tổng giờ của các hạng mục **chưa bị loại** — con số PM cân nhắc. */
    totalEstimateHours: z.number().nonnegative(),
    /**
     * Độ dài đường găng, tính bằng ngày — APM-1314.
     *
     * Đây là con số trả lời "kế hoạch này dài bao lâu", và nó **khác** tổng giờ:
     * 400 giờ chia cho năm người song song không phải 400 giờ trên trục thời gian.
     */
    criticalPathDays: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
    appliedAt: z.iso.datetime().nullable(),
});
// ── Ghi vào ─────────────────────────────────────────────────────────────────
export const GeneratePlanInput = z.object({
    projectId: z.uuid(),
    /** Mô tả dự án bằng lời. Luôn được đối xử như **dữ liệu**, không phải chỉ thị. */
    description: z.string().min(20).max(10_000),
    /** Template làm điểm xuất phát. Bỏ trống = để AI tự chia. */
    templateId: z.uuid().optional(),
    /**
     * Trần độ dài một hạng mục tầng 1, ngày. Bỏ trống = lấy `wbsMaxDays` của dự
     * án. Agile đặt nhỏ, Waterfall đặt lớn — spec 9.1.
     */
    granularityDays: z.number().int().min(1).max(60).optional(),
    /**
     * Mốc phải xong — APM-806, bước "bối cảnh" của wizard.
     *
     * Không suy ra từ `project.end_date`: người dùng thường sinh kế hoạch cho
     * **một phần** của dự án, và hạn của phần đó không phải hạn của cả dự án.
     */
    deadline: CalendarDate.optional(),
    /**
     * Ràng buộc bằng lời: "đội chỉ có 2 backend", "không được downtime", "phải
     * dùng lại hệ thống thanh toán cũ".
     *
     * Có trường riêng thay vì bắt người dùng nhồi vào `description`: mô tả dự án
     * là thứ họ dán từ tài liệu, còn ràng buộc là thứ họ nghĩ ra lúc lập kế
     * hoạch. Trộn hai thứ thì lần sinh lại phải sửa cả đoạn văn.
     */
    constraints: z.string().max(2_000).optional(),
    /**
     * Mã dấu vết do client sinh — APM-1302.
     *
     * Wizard mở kết nối theo dõi rồi mới gửi yêu cầu, nên người dùng thấy được
     * cả bước "đang đọc template" đầu tiên. Bỏ trống thì vẫn chạy, chỉ không ai
     * theo dõi.
     */
    runId: z.uuid().optional(),
});
/**
 * Trả lời vòng hỏi lại — APM-806.
 *
 * Gửi **tất cả** câu trả lời một lần: wizard hiện cả nhóm câu hỏi trên một
 * bước, và một API sửa-từng-câu sẽ cần đọc-rồi-ghi ở phía gọi.
 */
export const AnswerClarifyInput = z.object({
    answers: z.record(z.string().min(1).max(60), z.string().min(1).max(500)),
    /** Dấu vết để bảng theo dõi kể tiếp cùng một cuộc đối thoại — APM-1302. */
    runId: z.uuid().optional(),
});
export const UpdatePlanItemInput = z.object({
    title: z.string().trim().min(1).max(500).optional(),
    description: z.string().max(5_000).nullable().optional(),
    estimateHours: z.number().positive().max(2_000).nullable().optional(),
    suggestedAssigneeId: z.uuid().nullable().optional(),
    startOffsetDays: z.number().int().min(0).max(3_650).nullable().optional(),
    durationDays: z.number().int().min(1).max(365).nullable().optional(),
    /** Bỏ hạng mục ra khỏi lần áp dụng mà vẫn giữ lại để biết vì sao bị loại. */
    excluded: z.boolean().optional(),
});
/**
 * Đề xuất **ai làm hạng mục nào** — APM-UX-ai4.
 *
 * ## Vì sao `itemId` chứ không phải chỉ số
 *
 * Khác `GeneratedItem.dependsOn`: ở đó model đang **sinh ra** danh sách nên nó
 * chỉ đếm được, còn ở đây các hạng mục đã tồn tại và tool `get_plan_draft` đưa
 * thẳng id cho model. Không có gì để đoán, nên không có lý do gì để đếm.
 *
 * ## Vì sao `unassigned` là một phần của hợp đồng
 *
 * "Không tìm được ai hợp" là một câu trả lời **đúng**, và nó hay xảy ra: hạng
 * mục cần một kỹ năng không ai trong dự án khai, hoặc mọi người đã kín giờ
 * trong đúng cửa sổ ấy. Không có chỗ nói ra thì model sẽ giao bừa cho người
 * còn trống nhất, và một lời giao bừa trông y hệt một lời giao có căn cứ.
 */
export const PlanAssignment = z.object({
    itemId: z.uuid(),
    userId: z.uuid(),
    /**
     * Vì sao **người này** cho **việc này**. Phải nêu cả kỹ năng lẫn giờ còn
     * trống: "phù hợp" không phải một lý do, nó là một kết luận không có căn cứ.
     */
    reason: z.string().min(1).max(500),
});
export const PlanAssignments = z.object({
    assignments: z.array(PlanAssignment).max(120),
    /** Hạng mục **cố ý** không giao, kèm lý do — xem chú thích trên. */
    unassigned: z
        .array(z.object({ itemId: z.uuid(), reason: z.string().min(1).max(500) }))
        .max(120),
});
/**
 * Áp đề xuất vào nháp — người bấm mới là lúc nó đi vào dữ liệu (luật #6).
 *
 * Vẫn chỉ chạm `ai_plan_draft_item.suggested_assignee_id`: **nháp**, chưa phải
 * task. Người phụ trách thật chỉ có khi bấm "Áp dụng" ở bước sau.
 */
export const ApplyPlanAssignmentsInput = z.object({
    assignments: z.array(z.object({ itemId: z.uuid(), userId: z.uuid() })).min(1).max(200),
});
export const ApplyPlanInput = z.object({
    /**
     * Ngày khởi động thật. `startOffsetDays` của từng hạng mục cộng vào đây ra
     * ngày tuyệt đối — nháp lưu lịch **tương đối** vì ngày khởi động có thể đổi
     * giữa lúc sinh và lúc duyệt.
     */
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export const ApplyPlanResult = z.object({
    draftId: z.uuid(),
    tasksCreated: z.number().int().nonnegative(),
    dependenciesCreated: z.number().int().nonnegative(),
    /** Hạng mục bị bỏ qua kèm lý do — người dùng cần biết cái gì đã không vào. */
    skipped: z.array(z.object({ title: z.string(), reason: z.string() })),
    /**
     * Số tín hiệu rủi ro dò được **ngay sau khi áp dụng** — APM-702, spec 12.1
     * đòi gợi ý rủi ro đi kèm lượt sinh kế hoạch đầu tiên.
     *
     * Là con số của bộ dò tất định, **không** phải gợi ý AI: chạy thêm một lượt
     * AI ở đây là tiêu tiền mà không ai bấm nút, và tính năng phải chạy được y
     * nguyên khi chưa có key. Màn hình đưa PM sang tab RAID để xem và (nếu muốn)
     * nhờ AI diễn đạt.
     */
    riskSignalCount: z.number().int().nonnegative(),
});
/**
 * Tinh chỉnh nháp bằng một câu yêu cầu — APM-603, spec mục 9 "review & refine".
 *
 * Model nhận **nháp hiện tại** (đã gồm cả sửa tay của người dùng) cộng lịch sử
 * yêu cầu, chứ không nhận lại toàn bộ hội thoại thô. Gửi ảnh chụp hiện tại rẻ
 * hơn, và quan trọng hơn: nó phản ánh đúng thứ người dùng đang nhìn thấy — phát
 * lại hội thoại cũ thì model làm việc trên một kế hoạch đã không còn tồn tại.
 */
export const RefinePlanInput = z.object({
    instruction: z.string().min(3).max(2_000),
    /**
     * Tài liệu người dùng muốn AI đọc trước khi trả lời — APM-ROLE-x2.
     *
     * Là **id**, không phải tên, và không nhét vào `instruction`: ô chọn tài liệu
     * cho ra dữ liệu có cấu trúc, nên nó đi như dữ liệu có cấu trúc. Nhét uuid
     * vào câu chữ thì lịch sử hội thoại mà người dùng đọc lại đầy id, và việc tìm
     * theo tên thì hỏng ngay khi hai tài liệu trùng tên.
     *
     * Trần 5: model đã có `list_project_documents` để tự tìm thêm, còn năm tài
     * liệu dài là đủ đầy cửa sổ ngữ cảnh.
     */
    documentIds: z.array(z.uuid()).max(5).optional(),
    /** Dấu vết — APM-1302. Xem `GeneratePlanInput.runId`. */
    runId: z.uuid().optional(),
});
export const AddPlanItemInput = z.object({
    title: z.string().trim().min(1).max(500),
    /** Có giá trị = subtask (tầng 2). "Chia nhỏ" một hạng mục chính là thêm con. */
    parentId: z.uuid().optional(),
    estimateHours: z.number().positive().max(2_000).optional(),
    startOffsetDays: z.number().int().min(0).max(3_650).optional(),
    durationDays: z.number().int().min(1).max(365).optional(),
});
//# sourceMappingURL=plan-draft.js.map